import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import { CaregiverId } from "../../../../shared/schema/schema";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";
import { ExtendedContactDetails, Field, FormState } from "./extended-contact-details.types";
import { FIELDS, toFormState, toRequestBody } from "./extended-contact-details.utils";

interface Props {
  caregiverId: CaregiverId;
  details: ExtendedContactDetails | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditExtendedContactDetailsModal(props: Props) {
  const { api } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState(() => toFormState(props.details));

  const save = useMutation({
    mutationFn: (body: ExtendedContactDetails) =>
      api.put("./caregivers/:caregiverId/extended_contact_details", {
        path: { caregiverId: props.caregiverId },
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["caregiver", props.caregiverId, "extended-contact-details"],
      });
      toast({
        title: "Extended contact details saved",
        status: "success",
        position: "top-right",
      });
      props.onClose();
    },
    onError: (error) => {
      toast({
        title: "Failed to save extended contact details",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Modal isOpen={props.isOpen} size="3xl" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Extended Contact Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={4}>
            {FIELDS.map((field) => (
              <FormControl key={field.key}>
                <FormLabel>{field.label}</FormLabel>
                <FieldInput
                  field={field}
                  value={form[field.key]}
                  onChange={(value) => setField(field.key, value)}
                />
              </FormControl>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isLoading={save.isPending}
            onClick={() => save.mutate(toRequestBody(form))}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function FieldInput(props: { field: Field; value: string; onChange: (value: string) => void }) {
  switch (props.field.type) {
    case "boolean":
      return (
        <Select
          placeholder="Not set"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
        >
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </Select>
      );
    case "textarea":
      return <Textarea value={props.value} onChange={(e) => props.onChange(e.target.value)} />;
    case "date":
      return (
        <Input type="date" value={props.value} onChange={(e) => props.onChange(e.target.value)} />
      );
    case "datetime":
      return (
        <Input
          type="datetime-local"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
        />
      );
    case "text":
      return <Input value={props.value} onChange={(e) => props.onChange(e.target.value)} />;
  }
}
